import { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ReferenceLine, ResponsiveContainer } from "recharts";
import { 
  BarChart3, TrendingUp, TrendingDown, Users, Star, MessageSquare,
  Download, Filter, RefreshCw, ThumbsUp, ThumbsDown, Minus,
  Smile, Frown, Meh, Award, Target, AlertTriangle, Bot, Tag,
  Clock, Zap, Activity, Bell, Building, X, ChevronDown, ChevronRight
} from "lucide-react";

export default function EncuestasResultadosPage() {
  const { getAuthHeaders, user } = useAuth();
  const esAdmin = ["admin", "administrador"].includes(String(user?.role || "").toLowerCase());
  const [limpiandoHuerfanas, setLimpiandoHuerfanas] = useState(false);
  const [loading, setLoading] = useState(true);
  const [campanas, setCampanas] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [selectedCampana, setSelectedCampana] = useState("");
  const [resultados, setResultados] = useState(null);
  const [miResumen, setMiResumen] = useState(null);
  const [metaNPS, setMetaNPS] = useState(60);
  const [exportandoHistorial, setExportandoHistorial] = useState(false);
  const [rankingEjecutivos, setRankingEjecutivos] = useState(null);
  const [tendencia, setTendencia] = useState([]);
  const [filtros, setFiltros] = useState({
    ejecutivo: "",
    aseguradora: "",
    supervisor: "",
    departamento: "",
    fecha_inicio: "",
    fecha_fin: ""
  });
  const [exportando, setExportando] = useState(false);
  
  // Estados para tiempo real
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(30); // segundos
  const [isLive, setIsLive] = useState(false);
  const refreshTimerRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    fetchCampanas();
    fetchDepartamentos();
    fetchMetaNPS();
    if (user?.solo_resultados_propios) fetchMiResumen();
    if ((user?.role || "").toLowerCase() === "supervisor") fetchRanking();
  }, []);

  const fetchMetaNPS = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/reportes-estadisticos/config`, getAuthHeaders());
      if (res.data?.meta_nps !== undefined && res.data?.meta_nps !== null) setMetaNPS(Number(res.data.meta_nps));
    } catch (err) {
      // fallback: meta 60
    }
  };

  const fetchRanking = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/resultados/ranking-ejecutivos`, getAuthHeaders());
      setRankingEjecutivos(res.data);
    } catch (err) {
      console.error("Error loading ranking ejecutivos:", err);
    }
  };

  const fetchMiResumen = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/resultados/mi-resumen-nps`, getAuthHeaders());
      setMiResumen(res.data);
    } catch (err) {
      console.error("Error loading mi resumen NPS:", err);
    }
  };

  const handleExportarMiHistorial = async () => {
    setExportandoHistorial(true);
    try {
      const res = await axios.get(`${API}/encuestas/resultados/mi-resumen-nps/exportar`, {
        ...getAuthHeaders(),
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `mi_historial_incentivos_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Historial exportado");
    } catch (err) {
      toast.error("Error al exportar el historial");
    } finally {
      setExportandoHistorial(false);
    }
  };

  const tendenciaPersonal = miResumen
    ? [
        ...[...(miResumen.historial || [])].reverse().map((h) => ({
          etiqueta: `${h.mes.slice(0, 3)} ${h.anio}`,
          nps: h.nps,
          base: h.base
        })),
        ...(miResumen.base > 0
          ? [{ etiqueta: `${miResumen.mes.slice(0, 3)} ${miResumen.anio}`, nps: miResumen.nps, base: miResumen.base }]
          : [])
      ]
    : [];

  const fetchDepartamentos = async () => {
    try {
      const res = await axios.get(`${API}/departamentos`, getAuthHeaders());
      setDepartamentos(res.data);
    } catch (err) {
      console.error("Error loading departamentos:", err);
    }
  };

  useEffect(() => {
    fetchResultados();
  }, [selectedCampana, filtros]);

  const handleLimpiarFiltros = () => {
    setSelectedCampana("");
    setFiltros({ ejecutivo: "", aseguradora: "", supervisor: "", departamento: "", fecha_inicio: "", fecha_fin: "" });
  };

  const [expandedDepts, setExpandedDepts] = useState({});
  const toggleDeptExpandido = (dept) => {
    setExpandedDepts((prev) => ({ ...prev, [dept]: !prev[dept] }));
  };

  // Auto-refresh timer
  useEffect(() => {
    if (autoRefresh && resultados) {
      refreshTimerRef.current = setInterval(() => {
        fetchResultados(true); // silent refresh
      }, refreshInterval * 1000);
    }
    
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, resultados, selectedCampana, filtros]);

  // WebSocket para actualizaciones en tiempo real
  useEffect(() => {
    if (!user) return;

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = API.replace('https://', '').replace('http://', '').replace('/api', '');
    const wsUrl = `${wsProtocol}//${wsHost}/api/ws/${user.id}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setIsLive(true);
        wsRef.current.send(JSON.stringify({
          action: "join_room",
          room_id: "encuestas_alertas"
        }));
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const mensaje = JSON.parse(event.data);
          if (mensaje.type === "encuesta_alerta") {
            // Nueva respuesta recibida, actualizar datos
            fetchResultados(true);
          }
        } catch (e) {}
      };
      
      wsRef.current.onclose = () => setIsLive(false);
      wsRef.current.onerror = () => setIsLive(false);
    } catch (e) {}
    
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [user]);

  const fetchCampanas = async () => {
    try {
      const res = await axios.get(`${API}/encuestas/resultados/campanas-visibles`, getAuthHeaders());
      setCampanas(res.data);
      // Cargar resultados generales al inicio
      fetchResultados();
    } catch (err) {
      toast.error("Error al cargar campañas");
    }
  };

  const fetchResultados = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      let url = `${API}/encuestas/resultados?`;
      if (selectedCampana) url += `campana_id=${selectedCampana}&`;
      if (filtros.ejecutivo) url += `ejecutivo=${filtros.ejecutivo}&`;
      if (filtros.aseguradora) url += `aseguradora=${filtros.aseguradora}&`;
      if (filtros.supervisor) url += `supervisor=${filtros.supervisor}&`;
      if (filtros.departamento) url += `departamento=${filtros.departamento}&`;
      if (filtros.fecha_inicio) url += `fecha_inicio=${filtros.fecha_inicio}&`;
      if (filtros.fecha_fin) url += `fecha_fin=${filtros.fecha_fin}&`;

      const [resResultados, resTendencia] = await Promise.all([
        axios.get(url, getAuthHeaders()),
        axios.get(`${API}/encuestas/resultados/tendencia${selectedCampana ? `?campana_id=${selectedCampana}` : ''}`, getAuthHeaders())
      ]);

      setResultados(resResultados.data);
      setTendencia(resTendencia.data);
      setLastUpdate(new Date());
    } catch (err) {
      if (!silent) toast.error("Error al cargar resultados");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleLimpiarHuerfanas = async () => {
    if (!window.confirm("¿Eliminar definitivamente las respuestas de campañas ya borradas? Esta acción no se puede deshacer.")) return;
    setLimpiandoHuerfanas(true);
    try {
      const res = await axios.delete(`${API}/encuestas/resultados/huerfanas`, getAuthHeaders());
      toast.success(res.data.message);
      fetchResultados(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al limpiar respuestas huérfanas");
    } finally {
      setLimpiandoHuerfanas(false);
    }
  };

  const handleExportar = async () => {
    if (!selectedCampana) {
      toast.error("Seleccione una campaña para exportar");
      return;
    }
    
    setExportando(true);
    try {
      const res = await axios.get(
        `${API}/encuestas/resultados/exportar?campana_id=${selectedCampana}`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `resultados_encuesta_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Archivo exportado");
    } catch (err) {
      toast.error("Error al exportar");
    } finally {
      setExportando(false);
    }
  };

  const getNPSColor = (nps) => {
    if (nps === null || nps === undefined) return "text-muted-foreground";
    return Number(nps) >= metaNPS ? "text-green-600" : "text-red-600";
  };

  const [expandedRows, setExpandedRows] = useState({});
  const toggleRow = (k) => setExpandedRows((p) => ({ ...p, [k]: !p[k] }));

  const MesesRow = ({ items, colSpan, testKey }) => (
    <TableRow data-testid={`meses-row-${testKey}`}>
      <TableCell colSpan={colSpan} className="bg-slate-50/80 p-3">
        {!items || items.length === 0 ? (
          <p className="text-center text-sm text-muted-foreground">Sin desglose mensual disponible</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {items.map((m) => (
              <div key={m.mes} className="border rounded-md bg-white px-3 py-2" data-testid={`mes-card-${testKey}-${m.mes}`}>
                <p className="text-xs font-semibold text-slate-700 mb-1">{m.mes_label}</p>
                <div className="flex items-center gap-3 text-xs text-slate-600">
                  <span>NPS <b className={getNPSColor(m.nps)}>{m.nps}</b></span>
                  <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />{m.satisfaccion_promedio ?? "-"}</span>
                  <span>{m.total_respuestas} resp.</span>
                  <span className="ml-auto flex gap-1">
                    <Badge variant="outline" className="bg-green-50 text-green-700 px-1.5 py-0 text-[10px]">{m.promotores}</Badge>
                    <Badge variant="outline" className="bg-yellow-50 text-yellow-700 px-1.5 py-0 text-[10px]">{m.pasivos}</Badge>
                    <Badge variant="outline" className="bg-red-50 text-red-700 px-1.5 py-0 text-[10px]">{m.detractores}</Badge>
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </TableCell>
    </TableRow>
  );

  const getNPSLabel = (nps) => {
    const color = Number(nps) >= metaNPS ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
    if (nps >= 70) return { label: "Excelente", color };
    if (nps >= 50) return { label: "Muy Bueno", color };
    if (nps >= 30) return { label: "Bueno", color };
    if (nps >= 0) return { label: "Regular", color };
    return { label: "Necesita Mejora", color };
  };

  const getSentimientoIcon = (sentimiento) => {
    switch (sentimiento) {
      case "positivo": return <Smile className="w-4 h-4 text-green-600" />;
      case "negativo": return <Frown className="w-4 h-4 text-red-600" />;
      default: return <Meh className="w-4 h-4 text-yellow-600" />;
    }
  };

  const formatLastUpdate = () => {
    if (!lastUpdate) return "";
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 1000);
    if (diff < 60) return `Hace ${diff}s`;
    if (diff < 3600) return `Hace ${Math.floor(diff / 60)}m`;
    return lastUpdate.toLocaleTimeString();
  };

  if (loading && !resultados) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-6 pt-14 md:pt-6 space-y-6" data-testid="encuestas-resultados-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Resultados de Encuestas
            {isLive && (
              <Badge variant="outline" className="ml-2 animate-pulse border-green-500 text-green-600">
                <Activity className="w-3 h-3 mr-1" />
                En Vivo
              </Badge>
            )}
          </h1>
          <div className="flex items-center gap-4 text-muted-foreground">
            <span>Análisis y métricas NPS de sus encuestas</span>
            {lastUpdate && (
              <span className="text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {formatLastUpdate()}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => fetchResultados(false)} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button onClick={handleExportar} disabled={exportando || !selectedCampana}>
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
          {esAdmin && (
            <Button variant="outline" onClick={handleLimpiarHuerfanas} disabled={limpiandoHuerfanas} title="Elimina definitivamente respuestas de campañas ya borradas" data-testid="limpiar-huerfanas-btn">
              {limpiandoHuerfanas ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Limpiar Huérfanas
            </Button>
          )}
        </div>
          {/* Controles de Auto-Refresh */}
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Switch
                id="auto-refresh"
                checked={autoRefresh}
                onCheckedChange={setAutoRefresh}
              />
              <Label htmlFor="auto-refresh" className="text-xs cursor-pointer">
                Auto-actualizar
              </Label>
            </div>
            {autoRefresh && (
              <Select value={refreshInterval.toString()} onValueChange={(v) => setRefreshInterval(parseInt(v))}>
                <SelectTrigger className="w-24 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15s</SelectItem>
                  <SelectItem value="30">30s</SelectItem>
                  <SelectItem value="60">1m</SelectItem>
                  <SelectItem value="120">2m</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
      </div>

      {/* Mi Resumen NPS (permiso Solo lectura resultados propios) */}
      {user?.solo_resultados_propios && miResumen && (
        <Card
          className={`border-2 ${miResumen.base === 0 ? "border-slate-200" : miResumen.alcanza_meta ? "border-green-500" : "border-red-500"}`}
          data-testid="mi-resumen-nps-card"
        >
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${miResumen.base === 0 ? "bg-slate-100 text-slate-500" : miResumen.alcanza_meta ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>
                  <Award className="w-8 h-8" />
                </div>
                <div>
                  <h2 className="font-bold text-lg">Mi Resumen NPS — {miResumen.mes} {miResumen.anio}</h2>
                  {miResumen.base === 0 ? (
                    <p className="text-sm text-muted-foreground">Aún no tienes respuestas de encuestas este mes.</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Meta del incentivo: {miResumen.meta_nps}% · Basado en {miResumen.base} encuesta{miResumen.base !== 1 ? "s" : ""} respondida{miResumen.base !== 1 ? "s" : ""}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-6">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">NPS del mes</p>
                  <p
                    className={`text-3xl font-bold ${miResumen.base === 0 ? "text-slate-400" : miResumen.alcanza_meta ? "text-green-600" : "text-red-600"}`}
                    data-testid="mi-nps-valor"
                  >
                    {miResumen.base === 0 ? "—" : `${miResumen.nps.toFixed(1)}%`}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase">Promedio</p>
                  <p className="text-2xl font-semibold">{miResumen.base === 0 ? "—" : miResumen.promedio.toFixed(2)}</p>
                </div>
                <div className="flex gap-3 text-center text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Smile className="w-3 h-3 text-green-500" /> Promotores</p>
                    <p className="font-semibold">{miResumen.promotores}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Meh className="w-3 h-3 text-amber-500" /> Pasivos</p>
                    <p className="font-semibold">{miResumen.pasivos}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Frown className="w-3 h-3 text-red-500" /> Detractores</p>
                    <p className="font-semibold">{miResumen.detractores}</p>
                  </div>
                </div>
                {miResumen.base > 0 && (
                  miResumen.alcanza_meta ? (
                    <Badge className="bg-green-600 hover:bg-green-600 text-white px-3 py-1.5 text-sm" data-testid="mi-incentivo-badge">
                      <Award className="w-4 h-4 mr-1" />
                      Alcanzas el incentivo
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="px-3 py-1.5 text-sm" data-testid="mi-incentivo-badge">
                      <Target className="w-4 h-4 mr-1" />
                      Por debajo de la meta
                    </Badge>
                  )
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mi Tendencia NPS (evolución mes a mes) */}
      {user?.solo_resultados_propios && tendenciaPersonal.length > 1 && (
        <Card data-testid="mi-tendencia-nps-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Mi Tendencia NPS
            </CardTitle>
            <CardDescription>Evolución de tu NPS mes a mes frente a la meta del {miResumen.meta_nps}%</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tendenciaPersonal} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="etiqueta" tick={{ fontSize: 12 }} />
                  <YAxis domain={[-100, 100]} tick={{ fontSize: 12 }} tickFormatter={(v) => `${v}%`} />
                  <RechartsTooltip
                    formatter={(value, name) => name === "nps" ? [`${Number(value).toFixed(1)}%`, "NPS"] : [value, name]}
                    labelFormatter={(l) => `Mes: ${l}`}
                  />
                  <ReferenceLine y={miResumen.meta_nps} stroke="#dc2626" strokeDasharray="6 4" label={{ value: `Meta ${miResumen.meta_nps}%`, position: "insideTopRight", fill: "#dc2626", fontSize: 11 }} />
                  <Line type="monotone" dataKey="nps" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: "#2563eb" }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historial de Incentivos (meses anteriores) */}
      {user?.solo_resultados_propios && miResumen?.historial?.length > 0 && (
        <Card data-testid="historial-incentivos-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Historial de Incentivos
                </CardTitle>
                <CardDescription>NPS logrado en tus meses anteriores y si ganaste el incentivo (meta {miResumen.meta_nps}%)</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={handleExportarMiHistorial} disabled={exportandoHistorial} data-testid="exportar-mi-historial-btn">
                {exportandoHistorial ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Exportar Excel
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mes</TableHead>
                    <TableHead className="text-center">Encuestas</TableHead>
                    <TableHead className="text-center">Promedio</TableHead>
                    <TableHead className="text-center">Promotores</TableHead>
                    <TableHead className="text-center">Pasivos</TableHead>
                    <TableHead className="text-center">Detractores</TableHead>
                    <TableHead className="text-center">NPS</TableHead>
                    <TableHead className="text-center">Incentivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {miResumen.historial.map((h, i) => (
                    <TableRow key={i} data-testid={`historial-mes-${h.anio}-${h.mes}`}>
                      <TableCell className="font-medium whitespace-nowrap">{h.mes} {h.anio}</TableCell>
                      <TableCell className="text-center">{h.base}</TableCell>
                      <TableCell className="text-center">{h.promedio.toFixed(2)}</TableCell>
                      <TableCell className="text-center">{h.promotores}</TableCell>
                      <TableCell className="text-center">{h.pasivos}</TableCell>
                      <TableCell className="text-center">{h.detractores}</TableCell>
                      <TableCell className={`text-center font-bold ${h.alcanza_meta ? "text-green-600" : "text-red-600"}`}>
                        {h.nps.toFixed(1)}%
                      </TableCell>
                      <TableCell className="text-center">
                        {h.alcanza_meta ? (
                          <Badge className="bg-green-600 hover:bg-green-600 text-white">
                            <Award className="w-3 h-3 mr-1" />
                            Ganado
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <Target className="w-3 h-3 mr-1" />
                            No alcanzado
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ranking de Ejecutivos (supervisores) */}
      {rankingEjecutivos && rankingEjecutivos.ranking && (
        <Card data-testid="ranking-ejecutivos-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Award className="w-4 h-4" />
              Ranking de Ejecutivos — {rankingEjecutivos.mes} {rankingEjecutivos.anio}
            </CardTitle>
            <CardDescription>NPS del mes de tus ejecutivos encuestados frente a la meta del {rankingEjecutivos.meta_nps}%</CardDescription>
          </CardHeader>
          <CardContent>
            {rankingEjecutivos.ranking.length === 0 ? (
              <p className="text-sm text-muted-foreground">Tus ejecutivos aún no tienen respuestas de encuestas este mes.</p>
            ) : (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 text-center">#</TableHead>
                      <TableHead>Ejecutivo</TableHead>
                      <TableHead className="text-center">Encuestas</TableHead>
                      <TableHead className="text-center">Promedio</TableHead>
                      <TableHead className="text-center">Promotores</TableHead>
                      <TableHead className="text-center">Pasivos</TableHead>
                      <TableHead className="text-center">Detractores</TableHead>
                      <TableHead className="text-center">NPS</TableHead>
                      <TableHead className="text-center">Incentivo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rankingEjecutivos.ranking.map((e, i) => (
                      <TableRow key={e.ejecutivo_id} data-testid={`ranking-fila-${i + 1}`}>
                        <TableCell className="text-center font-bold">
                          {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                        </TableCell>
                        <TableCell className="font-medium">{e.ejecutivo_nombre}</TableCell>
                        <TableCell className="text-center">{e.base}</TableCell>
                        <TableCell className="text-center">{e.promedio.toFixed(2)}</TableCell>
                        <TableCell className="text-center">{e.promotores}</TableCell>
                        <TableCell className="text-center">{e.pasivos}</TableCell>
                        <TableCell className="text-center">{e.detractores}</TableCell>
                        <TableCell className={`text-center font-bold ${e.alcanza_meta ? "text-green-600" : "text-red-600"}`}>
                          {e.nps.toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-center">
                          {e.alcanza_meta ? (
                            <Badge className="bg-green-600 hover:bg-green-600 text-white">
                              <Award className="w-3 h-3 mr-1" />
                              Alcanza
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <Target className="w-3 h-3 mr-1" />
                              Por debajo
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Campaña</Label>
              <Select value={selectedCampana || "all"} onValueChange={(v) => setSelectedCampana(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="filtro-campana">
                  <SelectValue placeholder="Todas las campañas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las campañas</SelectItem>
                  {campanas.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Ejecutivo</Label>
              <Input
                placeholder="Filtrar por ejecutivo"
                value={filtros.ejecutivo}
                onChange={(e) => setFiltros({ ...filtros, ejecutivo: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Supervisor</Label>
              <Input
                placeholder="Filtrar por supervisor"
                value={filtros.supervisor}
                onChange={(e) => setFiltros({ ...filtros, supervisor: e.target.value })}
                data-testid="filtro-supervisor"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Departamento</Label>
              <Select 
                value={filtros.departamento || "all"} 
                onValueChange={(v) => setFiltros({ ...filtros, departamento: v === "all" ? "" : v })}
              >
                <SelectTrigger data-testid="filtro-departamento">
                  <SelectValue placeholder="Todos los departamentos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los departamentos</SelectItem>
                  {departamentos.map((d) => (
                    <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Aseguradora</Label>
              <Input
                placeholder="Filtrar por aseguradora"
                value={filtros.aseguradora}
                onChange={(e) => setFiltros({ ...filtros, aseguradora: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Desde (creación)</Label>
              <Input
                type="date"
                value={filtros.fecha_inicio}
                onChange={(e) => setFiltros({ ...filtros, fecha_inicio: e.target.value })}
                data-testid="filtro-fecha-inicio"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Hasta (creación)</Label>
              <Input
                type="date"
                value={filtros.fecha_fin}
                onChange={(e) => setFiltros({ ...filtros, fecha_fin: e.target.value })}
                data-testid="filtro-fecha-fin"
              />
            </div>
            <div className="flex items-end">
              <Button variant="outline" className="w-full" onClick={handleLimpiarFiltros} data-testid="limpiar-filtros-btn">
                <X className="w-4 h-4 mr-2" />
                Limpiar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {!resultados || resultados.total_respuestas === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BarChart3 className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Sin datos</h3>
            <p className="text-muted-foreground">No hay respuestas que coincidan con los filtros seleccionados</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* KPIs Principales */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* NPS Score */}
            <Card className="col-span-1 md:col-span-2">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Net Promoter Score (NPS)</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className={`text-5xl font-bold ${getNPSColor(resultados.nps)}`}>
                        {resultados.nps !== null ? resultados.nps : '-'}
                      </span>
                      {resultados.nps !== null && (
                        <Badge className={getNPSLabel(resultados.nps).color}>
                          {getNPSLabel(resultados.nps).label}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Target className="w-12 h-12 text-muted-foreground" />
                </div>
                
                {/* Distribución NPS */}
                <div className="mt-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ThumbsUp className="w-4 h-4 text-green-600" />
                      <span className="text-sm">Promotores (9-10)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{resultados.distribucion_nps.promotores}</span>
                      <span className="text-xs text-muted-foreground">({resultados.distribucion_nps.promotores_pct}%)</span>
                    </div>
                  </div>
                  <Progress value={resultados.distribucion_nps.promotores_pct} className="h-2 bg-green-100" />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Minus className="w-4 h-4 text-yellow-600" />
                      <span className="text-sm">Pasivos (7-8)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{resultados.distribucion_nps.pasivos}</span>
                      <span className="text-xs text-muted-foreground">({resultados.distribucion_nps.pasivos_pct}%)</span>
                    </div>
                  </div>
                  <Progress value={resultados.distribucion_nps.pasivos_pct} className="h-2 bg-yellow-100" />

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ThumbsDown className="w-4 h-4 text-red-600" />
                      <span className="text-sm">Detractores (0-6)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{resultados.distribucion_nps.detractores}</span>
                      <span className="text-xs text-muted-foreground">({resultados.distribucion_nps.detractores_pct}%)</span>
                    </div>
                  </div>
                  <Progress value={resultados.distribucion_nps.detractores_pct} className="h-2 bg-red-100" />
                </div>
              </CardContent>
            </Card>

            {/* Satisfacción */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-muted-foreground">Satisfacción Promedio</p>
                  <Star className="w-5 h-5 text-yellow-500" />
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold" data-testid="satisfaccion-promedio-valor">
                    {resultados.satisfaccion_promedio || '-'}
                  </span>
                  <span className="text-muted-foreground">/ 10</span>
                </div>
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star
                      key={i}
                      className={`w-5 h-5 ${
                        i <= Math.round((resultados.satisfaccion_promedio || 0) / 2)
                          ? 'text-yellow-500 fill-yellow-500'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Total Respuestas */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-medium text-muted-foreground">Total Respuestas</p>
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-4xl font-bold">{resultados.total_respuestas}</div>
                <div className="flex gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Smile className="w-3 h-3 text-green-600" />
                    {resultados.sentimientos.positivo}
                  </span>
                  <span className="flex items-center gap-1">
                    <Meh className="w-3 h-3 text-yellow-600" />
                    {resultados.sentimientos.neutro}
                  </span>
                  <span className="flex items-center gap-1">
                    <Frown className="w-3 h-3 text-red-600" />
                    {resultados.sentimientos.negativo}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs de análisis detallado */}
          <Tabs defaultValue="ejecutivos" className="w-full">
            <TabsList>
              <TabsTrigger value="ejecutivos">Por Ejecutivo</TabsTrigger>
              <TabsTrigger value="aseguradoras">Por Aseguradora</TabsTrigger>
              <TabsTrigger value="supervisores">Por Supervisor</TabsTrigger>
              <TabsTrigger value="departamentos">Por Departamento</TabsTrigger>
              <TabsTrigger value="tendencia">Tendencia</TabsTrigger>
              <TabsTrigger value="comentarios">Comentarios</TabsTrigger>
            </TabsList>

            <TabsContent value="ejecutivos" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Ranking de Ejecutivos
                  </CardTitle>
                  <CardDescription>NPS y satisfacción por ejecutivo</CardDescription>
                </CardHeader>
                <CardContent>
                  {resultados.por_ejecutivo.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Sin datos de ejecutivos</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Ejecutivo</TableHead>
                          <TableHead>Departamento (NPS Depto)</TableHead>
                          <TableHead className="text-center">NPS Ejecutivo</TableHead>
                          <TableHead className="text-center">Satisfacción</TableHead>
                          <TableHead className="text-center">Respuestas</TableHead>
                          <TableHead className="text-right">Distribución</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resultados.por_ejecutivo.map((ej, idx) => (
                          <Fragment key={`${ej.ejecutivo}||${ej.encuesta || ""}`}>
                          <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRow(`ej-${ej.ejecutivo}||${ej.encuesta || ""}`)} data-testid={`ejecutivo-row-${idx}`}>
                            <TableCell className="font-medium">{idx + 1}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {expandedRows[`ej-${ej.ejecutivo}||${ej.encuesta || ""}`]
                                  ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                                <div className="flex flex-col">
                                  <span>{ej.ejecutivo}</span>
                                  {ej.encuesta && (
                                    <span className="text-xs text-muted-foreground" data-testid={`ejecutivo-encuesta-${idx}`}>
                                      {ej.encuesta}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {ej.departamento || "Sin departamento"}
                                  {ej.sub_departamento && (
                                    <span className="text-muted-foreground ml-1">({ej.sub_departamento})</span>
                                  )}
                                </span>
                                <span className={`text-sm font-bold ${getNPSColor(ej.departamento_nps)}`}>
                                  NPS Depto: {ej.departamento_nps || 0}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold ${getNPSColor(ej.nps)}`}>{ej.nps}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              {ej.satisfaccion_promedio ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                  {ej.satisfaccion_promedio}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">{ej.total_respuestas}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Badge variant="outline" className="bg-green-50 text-green-700">{ej.promotores}</Badge>
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700">{ej.pasivos}</Badge>
                                <Badge variant="outline" className="bg-red-50 text-red-700">{ej.detractores}</Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedRows[`ej-${ej.ejecutivo}||${ej.encuesta || ""}`] && <MesesRow items={ej.desglose_mensual} colSpan={7} testKey={`ej-${idx}`} />}
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="aseguradoras" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>NPS por Aseguradora</CardTitle>
                  <CardDescription>Satisfacción de clientes por aseguradora</CardDescription>
                </CardHeader>
                <CardContent>
                  {resultados.por_aseguradora.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Sin datos de aseguradoras</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Aseguradora</TableHead>
                          <TableHead className="text-center">NPS</TableHead>
                          <TableHead className="text-center">Satisfacción</TableHead>
                          <TableHead className="text-center">Respuestas</TableHead>
                          <TableHead className="text-right">Distribución</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resultados.por_aseguradora.map((aseg, aidx) => (
                          <Fragment key={aseg.aseguradora}>
                          <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRow(`aseg-${aseg.aseguradora}`)} data-testid={`aseguradora-row-${aidx}`}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-1">
                                {expandedRows[`aseg-${aseg.aseguradora}`]
                                  ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                                {aseg.aseguradora}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold ${getNPSColor(aseg.nps)}`}>{aseg.nps}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              {aseg.satisfaccion_promedio ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                  {aseg.satisfaccion_promedio}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">{aseg.total_respuestas}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Badge variant="outline" className="bg-green-50 text-green-700">{aseg.promotores}</Badge>
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700">{aseg.pasivos}</Badge>
                                <Badge variant="outline" className="bg-red-50 text-red-700">{aseg.detractores}</Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedRows[`aseg-${aseg.aseguradora}`] && <MesesRow items={aseg.desglose_mensual} colSpan={5} testKey={`aseg-${aidx}`} />}
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="supervisores" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    NPS por Supervisor
                  </CardTitle>
                  <CardDescription>Satisfacción de clientes agrupada por supervisor</CardDescription>
                </CardHeader>
                <CardContent>
                  {resultados.por_supervisor?.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Sin datos de supervisores</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Supervisor</TableHead>
                          <TableHead className="text-center">NPS</TableHead>
                          <TableHead className="text-center">Satisfacción</TableHead>
                          <TableHead className="text-center">Respuestas</TableHead>
                          <TableHead className="text-right">Distribución</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resultados.por_supervisor?.map((sup, idx) => (
                          <Fragment key={sup.supervisor}>
                          <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => toggleRow(`sup-${sup.supervisor}`)} data-testid={`supervisor-row-${idx}`}>
                            <TableCell className="font-medium">{idx + 1}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {expandedRows[`sup-${sup.supervisor}`]
                                  ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                                {sup.supervisor}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={`font-bold ${getNPSColor(sup.nps)}`}>{sup.nps}</span>
                            </TableCell>
                            <TableCell className="text-center">
                              {sup.satisfaccion_promedio ? (
                                <div className="flex items-center justify-center gap-1">
                                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                  {sup.satisfaccion_promedio}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">{sup.total_respuestas}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Badge variant="outline" className="bg-green-50 text-green-700">{sup.promotores}</Badge>
                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700">{sup.pasivos}</Badge>
                                <Badge variant="outline" className="bg-red-50 text-red-700">{sup.detractores}</Badge>
                              </div>
                            </TableCell>
                          </TableRow>
                          {expandedRows[`sup-${sup.supervisor}`] && <MesesRow items={sup.desglose_mensual} colSpan={6} testKey={`sup-${idx}`} />}
                          </Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pestaña por Departamento */}
            <TabsContent value="departamentos" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    Resultados por Departamento
                  </CardTitle>
                  <CardDescription>NPS y satisfacción por departamento</CardDescription>
                </CardHeader>
                <CardContent>
                  {!resultados.por_departamento || resultados.por_departamento.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Sin datos de departamentos</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[50px]">#</TableHead>
                          <TableHead>Departamento</TableHead>
                          <TableHead>Sub-departamento</TableHead>
                          <TableHead className="text-center">NPS</TableHead>
                          <TableHead className="text-center">Satisfacción</TableHead>
                          <TableHead className="text-center">Respuestas</TableHead>
                          <TableHead className="text-right">Distribución</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {resultados.por_departamento.map((dept, idx) => {
                          const tieneSubs = dept.sub_departamentos && dept.sub_departamentos.length > 0;
                          const expandido = !!expandedDepts[dept.departamento];
                          return (
                            <Fragment key={dept.departamento || idx}>
                              <TableRow
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => toggleDeptExpandido(dept.departamento)}
                                data-testid={`dept-row-${idx}`}
                              >
                                <TableCell className="font-medium">{idx + 1}</TableCell>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-1">
                                    {(expandido
                                      ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                      : <ChevronRight className="w-4 h-4 text-muted-foreground" />)}
                                    {dept.departamento || "Sin departamento"}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {tieneSubs ? (
                                    <Badge variant="outline" className="text-xs" data-testid={`dept-subs-badge-${idx}`}>
                                      {dept.sub_departamentos.length} sub-dpto{dept.sub_departamentos.length > 1 ? "s" : ""}
                                    </Badge>
                                  ) : (dept.sub_departamento || "-")}
                                </TableCell>
                                <TableCell className={`text-center font-bold ${getNPSColor(dept.nps)}`}>
                                  {dept.nps !== null ? dept.nps : '-'}
                                </TableCell>
                                <TableCell className="text-center">
                                  {dept.satisfaccion?.toFixed(1) || '-'}
                                  {dept.satisfaccion && <Star className="w-3 h-3 inline ml-1 text-yellow-500" />}
                                </TableCell>
                                <TableCell className="text-center">{dept.total_respuestas}</TableCell>
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-1 text-xs">
                                    <span className="text-green-600">{dept.promotores || 0}P</span>
                                    <span className="text-gray-500">{dept.pasivos || 0}N</span>
                                    <span className="text-red-600">{dept.detractores || 0}D</span>
                                  </div>
                                </TableCell>
                              </TableRow>
                              {expandido && <MesesRow items={dept.desglose_mensual} colSpan={7} testKey={`dept-${idx}`} />}
                              {tieneSubs && expandido && dept.sub_departamentos.map((sub, sidx) => (
                                <TableRow key={`${dept.departamento}-sub-${sidx}`} className="bg-muted/30" data-testid={`subdept-row-${idx}-${sidx}`}>
                                  <TableCell></TableCell>
                                  <TableCell className="pl-8 text-sm text-muted-foreground">↳ {dept.departamento}</TableCell>
                                  <TableCell className="text-sm font-medium">{sub.sub_departamento}</TableCell>
                                  <TableCell className={`text-center font-bold ${getNPSColor(sub.nps)}`}>
                                    {sub.nps !== null ? sub.nps : '-'}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {sub.satisfaccion?.toFixed(1) || '-'}
                                    {sub.satisfaccion && <Star className="w-3 h-3 inline ml-1 text-yellow-500" />}
                                  </TableCell>
                                  <TableCell className="text-center">{sub.total_respuestas}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1 text-xs">
                                      <span className="text-green-600">{sub.promotores || 0}P</span>
                                      <span className="text-gray-500">{sub.pasivos || 0}N</span>
                                      <span className="text-red-600">{sub.detractores || 0}D</span>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="tendencia" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Tendencia NPS
                  </CardTitle>
                  <CardDescription>Evolución del NPS en los últimos meses</CardDescription>
                </CardHeader>
                <CardContent>
                  {tendencia.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Sin datos de tendencia</p>
                  ) : (
                    <div className="space-y-4">
                      {/* Gráfico simple con barras */}
                      <div className="flex items-end justify-around h-48 gap-2">
                        {tendencia.map((t, idx) => {
                          const height = Math.max(10, Math.abs(t.nps) + 50);
                          return (
                            <div key={t.mes} className="flex flex-col items-center gap-2 flex-1">
                              <span className={`text-sm font-bold ${getNPSColor(t.nps)}`}>{t.nps}</span>
                              <div 
                                className={`w-full rounded-t transition-all ${
                                  t.nps >= metaNPS ? 'bg-green-500' : 'bg-red-500'
                                }`}
                                style={{ height: `${height}%` }}
                              />
                              <span className="text-xs text-muted-foreground">{t.mes}</span>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Tabla detalle */}
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mes</TableHead>
                            <TableHead className="text-center">NPS</TableHead>
                            <TableHead className="text-center">Respuestas</TableHead>
                            <TableHead className="text-center">Promotores</TableHead>
                            <TableHead className="text-center">Pasivos</TableHead>
                            <TableHead className="text-center">Detractores</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tendencia.map((t) => (
                            <TableRow key={t.mes}>
                              <TableCell className="font-medium">{t.mes}</TableCell>
                              <TableCell className="text-center">
                                <span className={`font-bold ${getNPSColor(t.nps)}`}>{t.nps}</span>
                              </TableCell>
                              <TableCell className="text-center">{t.total_respuestas}</TableCell>
                              <TableCell className="text-center text-green-600">{t.promotores}</TableCell>
                              <TableCell className="text-center text-yellow-600">{t.pasivos}</TableCell>
                              <TableCell className="text-center text-red-600">{t.detractores}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="comentarios" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Comentarios de Clientes
                  </CardTitle>
                  <CardDescription>
                    Análisis de sentimiento con IA - Los comentarios negativos aparecen primero
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {resultados.comentarios_recientes.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Sin comentarios</p>
                  ) : (
                    <div className="space-y-4">
                      {resultados.comentarios_recientes.map((com, idx) => {
                        // Extraer info del sentimiento (puede venir como objeto o string)
                        const sentimientoData = typeof com.sentimiento_data === 'object' ? com.sentimiento_data : null;
                        const sentimientoLabel = sentimientoData?.sentimiento || com.sentimiento || 'neutro';
                        const confianza = sentimientoData?.confianza;
                        const palabrasClave = sentimientoData?.palabras_clave || [];
                        const resumenIA = sentimientoData?.resumen;
                        const requiereAtencion = sentimientoData?.requiere_atencion;
                        const analizadoPor = sentimientoData?.analizado_por;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`p-4 rounded-lg border ${
                              sentimientoLabel === 'negativo' ? 'border-red-200 bg-red-50/50' :
                              sentimientoLabel === 'positivo' ? 'border-green-200 bg-green-50/50' :
                              'border-gray-200 bg-gray-50/50'
                            }`}
                          >
                            {/* Encabezado con sentimiento y NPS */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                {getSentimientoIcon(sentimientoLabel)}
                                <Badge variant={
                                  sentimientoLabel === 'negativo' ? 'destructive' :
                                  sentimientoLabel === 'positivo' ? 'default' :
                                  'secondary'
                                } className="capitalize">
                                  {sentimientoLabel}
                                </Badge>
                                {com.nps_score !== null && (
                                  <Badge variant="outline">NPS: {com.nps_score}</Badge>
                                )}
                                {requiereAtencion && (
                                  <Badge variant="destructive" className="flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" />
                                    Requiere Atención
                                  </Badge>
                                )}
                                {analizadoPor === 'ia' && (
                                  <Badge variant="outline" className="flex items-center gap-1 text-blue-600 border-blue-200">
                                    <Bot className="w-3 h-3" />
                                    Analizado por IA
                                  </Badge>
                                )}
                                {confianza && (
                                  <span className="text-xs text-muted-foreground">
                                    {(confianza * 100).toFixed(0)}% confianza
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {com.fecha ? new Date(com.fecha).toLocaleDateString() : ''}
                              </span>
                            </div>
                            
                            {/* Comentario */}
                            <blockquote className="text-sm italic border-l-4 pl-3 mb-3 text-slate-700" 
                              style={{ 
                                borderColor: sentimientoLabel === 'negativo' ? '#ef4444' : 
                                             sentimientoLabel === 'positivo' ? '#22c55e' : '#9ca3af'
                              }}>
                              "{com.comentario}"
                            </blockquote>
                            
                            {/* Resumen de IA */}
                            {resumenIA && (
                              <div className="text-xs bg-white/60 p-2 rounded mb-2 flex items-start gap-2">
                                <Bot className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                                <span className="text-slate-600">{resumenIA}</span>
                              </div>
                            )}
                            
                            {/* Palabras clave */}
                            {palabrasClave.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap mb-2">
                                <Tag className="w-3 h-3 text-muted-foreground" />
                                {palabrasClave.map((palabra, i) => (
                                  <Badge key={i} variant="outline" className={`text-xs ${
                                    sentimientoLabel === 'negativo' ? 'border-red-200 text-red-700 bg-red-50' :
                                    sentimientoLabel === 'positivo' ? 'border-green-200 text-green-700 bg-green-50' :
                                    ''
                                  }`}>
                                    {palabra}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            
                            {/* Info del ejecutivo */}
                            {com.ejecutivo && (
                              <p className="text-xs text-muted-foreground">
                                Ejecutivo: {com.ejecutivo}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
      </main>
    </div>
  );
}
